import {
  Alert,
  Box,
  Checkbox,
  CircularProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect } from 'react';

import { useCalDavStore } from '../../store';

const ColorIndicator: React.FC<{ color: string | null }> = ({ color }) => (
  <Box
    sx={{
      width: 16,
      height: 16,
      borderRadius: '50%',
      backgroundColor: color || '#888',
      mr: 1,
      flexShrink: 0,
    }}
  />
);

export const CalDavCalendarList: React.FC = observer(() => {
  const store = useCalDavStore();

  useEffect(() => {
    if (store.config) {
      store.fetchCalendars();
    }
  }, [store, store.config]);

  const handleToggle = useCallback(
    (calendarId: string, currentSelected: boolean) => {
      store.updateCalendarSelection(calendarId, !currentSelected);
    },
    [store],
  );

  if (!store.config) {
    return <Alert severity="info">Сначала настройте подключение к CalDav серверу</Alert>;
  }

  if (store.isCalendarsLoading && store.calendars.length === 0) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  const unavailableCalendars = store.calendars.filter((cal) => cal.syncStatus !== 'synced');

  return (
    <Stack spacing={1}>
      {store.error && (
        <Alert severity="error" onClose={() => store.clearError()}>
          {store.error}
        </Alert>
      )}

      {unavailableCalendars.length > 0 && (
        <Alert severity="warning">
          Некоторые календари недоступны на сервере. Они будут автоматически удалены при следующей
          синхронизации.
        </Alert>
      )}

      {store.calendars.length === 0 ? (
        <Typography color="text.secondary" textAlign="center" py={2}>
          Нет доступных календарей. Нажмите &quot;Обновить&quot; для загрузки.
        </Typography>
      ) : (
        <List dense disablePadding>
          {store.calendars.map((calendar) => (
            <ListItem
              key={calendar.id}
              disablePadding
              sx={{
                opacity: calendar.syncStatus === 'synced' ? 1 : 0.5,
              }}
            >
              <ListItemButton
                onClick={() => handleToggle(calendar.id, calendar.isSelected)}
                disabled={calendar.syncStatus !== 'synced'}
              >
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={calendar.isSelected}
                    disableRipple
                    disabled={calendar.syncStatus !== 'synced'}
                  />
                </ListItemIcon>
                <ColorIndicator color={calendar.color} />
                <ListItemText
                  primary={calendar.name}
                  secondary={calendar.syncStatus !== 'synced' ? 'Недоступен на сервере' : undefined}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      )}
    </Stack>
  );
});
